import { ExternalLink } from '@components/Button/ExternalLink';
import { spacing } from '@constants/spacing';
import { kitsLearnMore } from '@constants/URLs';
import { Box, Grid, styled } from '@mui/material';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { WizardPageContent, WizardPageLayout } from '@partful/shared/ui/layouts/WizardPageLayout';
import { LoadingMessage } from '@partful/shared/ui/LoadingMessage/LoadingMessage';
import { useTenant } from '@providers/Tenant';
import { GA } from '@services/gtag';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepContent } from 'src/components/StepElements';
import { useConfiguration } from 'src/providers/Configuration';
import { ReturnToProductLibraryButton } from '../components/ReturnToProductLibraryButton';
import { DownloadKits } from './components/DownloadKits';
import { UploadKits } from './components/UploadKits';

export function Kits() {
  const { t } = useTranslation('manageProduct');
  const { name: productName } = useConfiguration();
  const tenant = useTenant();
  const { accountId } = tenant;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const searchParams = useQueryParams();
  const { configurationPID } = searchParams;

  return (
    <>
      {isLoading ? (
        <WizardPageLayout style={{ paddingTop: 0 }}>
          <WizardPageContent>
            <LoadingMessage title={t('kits.loading.title')} message={t('kits.loading.message')} />
          </WizardPageContent>
        </WizardPageLayout>
      ) : null}
      <StepContent sx={{ display: isLoading ? 'none' : 'bloc' }}>
        <Grid container spacing={2} sx={{ py: '6rem' }}>
          <Grid item sm={12} md={6}>
            <StepContainer>
              <StepLabel>1</StepLabel>
              <div>
                <DownloadKits
                  account={accountId}
                  tenant={tenant}
                  productName={productName}
                  setIsLoading={setIsLoading}
                />
                <LearnMoreContainer>
                  <ExternalLink
                    href={kitsLearnMore}
                    onClick={() => {
                      GA.ManageProductPage.learnMoreKits(tenant, configurationPID, productName);
                    }}
                  >
                    {t('kits.templates.download.learnMore')}
                  </ExternalLink>
                </LearnMoreContainer>
              </div>
            </StepContainer>
          </Grid>
          <Grid item sm={12} md={6}>
            <StepContainer>
              <StepLabel>2</StepLabel>
              <UploadContainer>
                <StepTitle>{t('kits.templates.upload.title')}</StepTitle>
                <UploadKits />
              </UploadContainer>
            </StepContainer>
          </Grid>
        </Grid>
      </StepContent>
      <ReturnToProductLibraryButton />
    </>
  );
}

const StepContainer = styled(Box)`
  display: flex;
  gap: 1rem;
`;

const UploadContainer = styled('div')`
  flex: 1;
`;

const LearnMoreContainer = styled('p')`
  flex: 1;
  padding-top: ${spacing.smallSpacing};
`;

const StepLabel = styled('span')`
  ${({ theme }) => `border: 2px solid ${theme.palette.primary.dark};`}
  height: 3rem;
  width: 3rem;
  min-width: 3rem;
  display: flex;
  align-items: center;
  border-radius: 2rem;
  justify-content: center;
  margin-top: -1rem;
  font-size: large;
`;

const StepTitle = styled('h3')`
  ${({ theme }) => `
    font-size: 1rem;
    font-weight: ${theme.typography.fontWeightBold};
  `}
  margin-bottom: 1rem;
`;
